<template>
  <div class="weighin" ref="weighinRef">
    <div @click="handleOpen"
      class="d-flex reactions-btns items-center gap-2 cursor-pointer opacity-70 hover:opacity-100 transition ease-in-out duration-300">
      <img :src="icon" class="icons-color-filter" alt="">
      <span class="ml-2 truncate text-sm max-w-[200px] md:max-w-[500px] lg:max-w-[600px]">{{ textSettings }}</span>
    </div>

    <div v-if="isActive" v-click-outside="closeModal" ref="modal" class="weighin--tooltip"
      :class="{ 'active': isActive }">
      <div class="weighin-close" @click="closeModal">
        <div>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M1.39953 13.6553L0.345703 12.6015L5.9457 7.00148L0.345703 1.40148L1.39953 0.347656L6.99953 5.94766L12.5995 0.347656L13.6534 1.40148L8.05335 7.00148L13.6534 12.6015L12.5995 13.6553L6.99953 8.05531L1.39953 13.6553Z"
              fill="currentColor" />
          </svg>
        </div>
      </div>
      <div class="weighin-head">
        <div class="weighin-head-title">
          Who can weigh in on this post
        </div>
        <p>Set thresholds for each reputation category to limit who can react to your post.</p>
      </div>
      <div class="weighin-body px-[25px]">
        <div class="weighin-wrp gap-y-[10px] mt-[10px]">
          <div class="weighin-item reactions-react relative" v-for="(reaction, index) in reactionsCategories"
            :key="index">
            <span class="text-[12px] text-center -mb-5">{{ reaction.name[0].toUpperCase() +
              reaction.name.slice(1) }}</span>
            <div class="weighin-react-r">
              <weighinSelect v-model="reaction.value"
                @update:model-value="value => changedReaction(reaction, index, value)"
                :defsettings="userdata.weighInThresholds[index]" />
            </div>
          </div>
        </div>
        <div class="weighin-body px-[4px] mt-4">
          <label>
            <Checkbox v-model="checked" indeterminate binary class="weighin-checkbox mr-2" />
            Set as default settings
          </label>
        </div>
      </div>
      <div class="weighin-actions mt-4">
        <button color="weighin-actions-green" @click="closeModal">Cancel</button>
        <button class="weighin-actions-main" @click="submit">
          {{ loading ? 'Loading...' : 'Confirm' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  nextTick,
  onMounted,
  onBeforeMount,
  onUnmounted,
  ref,
} from 'vue';

import { directive as vClickOutside } from 'click-outside-vue3';
import Checkbox from 'primevue/checkbox';
import { useToast } from 'primevue/usetoast';
import { useStore } from 'vuex';

import weighinSelect from './weighinSelect.vue';

const props = defineProps(['post', 'disabled'])
const emits = defineEmits(['update:addReply'])

const store = useStore();

const checked = ref(false);
const inputText = ref('');
const textSettings = ref('Anyone can weigh in');
const changeMind = ref(false)
const positionTop = ref('0px')
const weighinRef = ref(null)
const userdata = ref(null)
const modal = ref(null)
const icon = ref('/icon/public.svg');

const reactionsCategories = computed(() => store.getters.getReputationCategories);

const toast = useToast();
const isActive = ref(false)
const loading = ref(false)

const closeModal = () => {
  checked.value = false;
  inputText.value = ''

  if (reactionsCategories.value) {
    reactionsCategories.value = reactionsCategories?.value.forEach(item => {
      item.value = null;
    })
  }
  changeMind.value = false
  isActive.value = false
  window.removeEventListener('scroll', handleScroll);
}

const handleScroll = () => {
  if (modal.value) {
    const rect = modal.value.getBoundingClientRect();
    const modalHeight = rect.height;

    if (rect.top + modalHeight * 0.7 < 0 || rect.bottom - modalHeight * 0.7 > window.innerHeight) {
      closeModal();
    }
  }
};

const handleOpen = async (event) => {
  userdata.value = await JSON.parse(localStorage.getItem("user"));
  const thresholds = userdata.value.weighInThresholds;
  if (thresholds[0].threshold === 'off' && thresholds[1].threshold === 'off' && thresholds[2].threshold === 'off' && thresholds[3].threshold === 'off' && thresholds[4].threshold === 'off') {
    textSettings.value = 'Anyone can weigh in'
  } else {
    textSettings.value = 'Specific people can weight in'
  }
  if (!userdata.value || props.disabled) {
    return
  }

  //const rect = weighinRef.value.getBoundingClientRect()
  //positionTop.value = -(rect.top - 80) + 'px';

  nextTick(() => {
    window.addEventListener('scroll', handleScroll);
  });

  isActive.value = true;
}

onMounted(async () => {

  if (!reactionsCategories.value) {
    await store.dispatch('fetchReputationCategories');
  }

  userdata.value = await JSON.parse(localStorage.getItem("user"));
  const thresholds = userdata.value.weighInThresholds;
  if (thresholds[0]) {
    if (thresholds[0].threshold === 'off' && thresholds[1].threshold === 'off' && thresholds[2].threshold === 'off' && thresholds[3].threshold === 'off' && thresholds[4].threshold === 'off') {
      textSettings.value = 'Anyone can weigh in'
    } else {
      textSettings.value = 'Specific people can weight in'
    }
    if (reactionsCategories.value) {
      thresholds.forEach((item, index) => {
        if (reactionsCategories.value[index] && item.threshold) {
          reactionsCategories.value[index].threshold = item.threshold;
        }
      });
    }
  }
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const submit = async () => {
  const data = [];
  reactionsCategories.value.forEach((item, index) => {
    let level = "off"
    if (item.value == 0) level = 'off';
    if (item.value && item.value === 1) level = 'low';
    if (item.value && item.value === 2) level = 'medium';
    if (item.value && item.value === 3) level = 'high';
    data.push(
      {
        reputationCategoryId: item.id,
        threshold: item.value || item.value == 0 ? level : reactionsCategories.value[index].threshold
      }
    )
  });
  loading.value = true;
  if (checked.value) {
    try {
      const threholdsResp = await fetch(
        `/api/users/weigh-in-thresholds`,
        {
          method: 'POST',
          body: JSON.stringify({
            thresholds: data
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (threholdsResp.status !== 200 && threholdsResp.status !== 201) {
        toast.add({
          severity: 'error',
          summary: 'Thresholds not created',
          life: 7000,
        });
        return
      } else {
        if (data[0].threshold === 'off' && data[1].threshold === 'off' && data[2].threshold === 'off' && data[3].threshold === 'off' && data[4].threshold === 'off') {
          textSettings.value = 'Anyone can weigh in'
        } else {
          textSettings.value = 'Specific people can weight in'
        }
        toast.add({
          severity: "success",
          summary: "Default settings thresholds add",
          life: 7000,
        });
        store.dispatch('getMe');
        closeModal();
      }
    } catch (error) {
      console.log('error', error);
      toast.add({
        severity: 'error',
        summary: 'An unexpected error occurred',
        life: 7000,
      });
    } finally {
      loading.value = false;
    }
  } else {
    if (data[0].threshold === 'off' && data[1].threshold === 'off' && data[2].threshold === 'off' && data[3].threshold === 'off' && data[4].threshold === 'off') {
      textSettings.value = 'Anyone can weigh in'
    } else {
      textSettings.value = 'Specific people can weight in'
    }
    localStorage.setItem("thresholdsPost", JSON.stringify(data));
    loading.value = false;
    toast.add({
      severity: "success",
      summary: "Thresholds on this post was added",
      life: 7000,
    });
    icon.value = '/icon/person.svg'
    textSettings.value = getChoosedWeightInText(data);
    closeModal();
  }
};

const changedReaction = (value, index, v) => {
  reactionsCategories.value[index].threshold = value.threshold;
}

const getChoosedWeightInText = (data) => {
  const text = data.map(item => {
    const reaction = reactionsCategories.value.find(reaction => item.reputationCategoryId === reaction.id);
    if (reaction) {
      const formattedName = reaction.name.charAt(0).toUpperCase() + reaction.name.slice(1, 3);
      return `${formattedName} ${reaction.threshold}`;
    }
    return '';
  }).filter(Boolean)
    .join('.; ');

  return `Custom: ${text}`;
}

</script>

<style scoped lang="scss">
.weighin {
  font-size: 14px;
  position: relative;

  &-body {
    overflow: auto !important;
  }

  .text-red {
    color: #DC2626;
  }

  &-btn {
    display: flex;
    align-items: center;
    @apply space-x-[-12px];
    transition: .2s ease-in-out;

    &:hover {
      transform: scale(1.1);
    }
  }

  &-btns {
    cursor: pointer;
  }

  &-txt {
    font-size: 12px;
    color: #A1A1AA;
  }

  &-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    border-radius: 0 0 12px 12px;
    overflow: hidden;

    @include _639 {
      overflow: unset;
    }

    button:nth-child(1) {
      background: var(--background-primary);
      color: var(--foreground-primary) !important;
      cursor: pointer;

      &:hover {
        background: var(--background-btn-secondary);
      }
    }

    button:nth-child(2) {
      background: #A3E635;
      cursor: pointer;
      color: #09090B;

      &:hover {
        background: #86c027;
      }

      &.disabled {
        pointer-events: none;
        background: var(--background-secondary) !important;
        color: var(--foreground-primary) !important;
      }
    }

    button {
      height: 44px;
      transition: .3s;
      display: flex;
      text-align: center;
      font-weight: 500;
      justify-content: center;
      align-items: center;
    }
  }

  &-form {
    //background: #09090B;
    //border-left: 2px solid #48484A;
    //border-right: 2px solid #48484A;
    padding: 12px 14px;
  }

  &-total {
    font-size: 16px;
    font-family: 'ClashDislay', serif;
  }

  &-item {
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  &-wrp {
    display: grid;
    grid-template-columns: 1fr;

    .weighin-item {
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 0.5rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
  }

  &-close {
    height: 48px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-right: 20px;
    width: 100%;
    cursor: pointer;
  }

  &-head {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
    padding: 12px 25px;

    &-title {
      font-size: 18px;
    }
  }

  &-label {
    font-size: 14px;
  }

  &-price {
    font-size: 9px;
    font-weight: 500;
    font-family: 'ClashDislay', serif;
  }

  &-body {
    //background: #09090B;
    //border-left: 1px solid #48484A;
    //border-right: 1px solid #48484A;
    //border-top: 2px solid #48484A;
    //border-bottom: 2px solid #48484A;
  }


  &-item {
    padding: 10px 0;
    background: var(--reaction-item-bg);
    border-radius: 10px;
    //border-bottom: 2px solid #48484A;
    //border-left: 1px solid #48484A;
    //border-right: 1px solid #48484A;


    &-head {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 5px;

      img {
        margin-left: 5px;
        width: 10px;
        height: 10px;
      }
    }
  }

  &-react {
    display: flex;
    align-items: center;
    padding-left: 5px;
    padding-right: 8px;

    &-r {
      width: 100%;
      padding: 0 10px;
    }
  }
}

.weighin--tooltip {
  display: none;
  background: var(--background-primary);
  border: 1px solid rgba(255, 255, 255, 0.1);

  position: absolute;
  width: 420px;
  left: -10%;
  bottom: -50px;
  border-radius: 13px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.3);
  height: fit-content;

  @include _639 {
    position: fixed;
    top: 0 !important;
    left: 0;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    padding-bottom: 80px;
  }

  &.active {
    display: flex;
    flex-direction: column;
    animation: slide 350ms ease-in-out forwards;

    @keyframes slide {
      0% {
        opacity: 0;
        transform: scale(0.1) translateY(0%);
      }

      100% {
        opacity: 1;
        transform: scale(1);
      }
    }
  }
}

@media (max-height: 600px) {
  .weighin--tooltip {
    height: 593px !important;
    bottom: -20px !important;
  }

  .weighin-body {
    overflow: auto !important;
  }
}

@media (max-height: 720px) {
  .weighin--tooltip {
    height: 670px;
    //bottom: -6px !important;
  }
}

@media (min-height: 1024px) {
  .weighin--tooltip {
    bottom: -6px !important;
  }
}

:deep(.p-checkbox-box) {
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #1d1d21 !important;
  width: 18px !important;
  height: 18px !important;
}

.icons-color-filter {
  filter: var(--icons-color-filter);
}
</style>